
import { Component, Prop, Vue } from "vue-property-decorator";
import { configStore } from "@/store/store";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import { DynamicField } from "@/types/dynamic-data";
import DynamicDataField from "@/components/Reusable/ControlsTree/DynamicData/DynamicDataField.vue";
import { getDynamicPathAcceptsItems } from "@/lib/dynamic-data/utils";
import { DynamicFieldReturnType } from "@/types/dynamic-property-path";
import ToggleInput from "@/components/Reusable/CustomInputs/ToggleInput.vue";

type Category = {
  name: string;
  fields: DynamicField[];
};

@Component({
  name: "DynamicDataFields",
  components: { DynamicDataField, TextInput, DropdownInput, ToggleInput }
})
export default class DynamicDataFields extends Vue {
  @Prop()
  accepts!: DynamicFieldReturnType | false;

  @Prop()
  activeFieldSlug!: string | undefined;

  search: string | null = "";
  type: DynamicFieldReturnType | null = null;

  disableTypeSafetyCheck = false;

  get availableTypes() {
    return getDynamicPathAcceptsItems();
  }

  get internalType() {
    return this.type || this.accepts || "all";
  }

  get dynamicFields() {
    return configStore.dynamicFields;
  }

  get filteredFields() {
    return this.dynamicFields
      .filter(field => {
        // Search by label
        return field.label
          .toLowerCase()
          .includes(this.search ? this.search.toLowerCase() : "");
      })
      .filter(field => {
        // Filter by type
        return (
          this.internalType === "all" ||
          field.returnTypes.includes(this.internalType) ||
          field.returnTypes.includes("all")
        );
      });
  }

  get categories() {
    const categories = this.groupBy(this.filteredFields, "category");

    return categories.map(category => {
      const subcategories = this.groupBy(category.fields, "subcategory");

      return {
        name: category.name,
        subcategories
      };
    });
  }

  groupBy(items: DynamicField[], key: "category" | "subcategory"): Category[] {
    const grouped = items.reduce((categories: Record<string, any>, item) => {
      const name = item[key] || "";
      const category = categories[name] || {
        name,
        fields: []
      };

      category.fields.push(item);
      categories[name] = category;

      return categories;
    }, {});

    return Object.values(grouped);
  }

  setField(field: DynamicField) {
    if (!this.isFieldAvailable(field)) {
      return;
    }

    this.$emit("input", field);
  }

  updateType(newType: DynamicFieldReturnType) {
    this.type = newType;
  }

  isFieldAvailable(field: DynamicField) {
    return (
      this.disableTypeSafetyCheck ||
      !this.accepts ||
      this.accepts === "all" ||
      field.returnTypes.includes(this.accepts) ||
      field.returnTypes.includes("all")
    );
  }
}
